import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { env } from '@config/env';
import type { ReactNode } from 'react';

/**
 * Shared query configuration
 * Can be imported elsewhere for consistent retry/caching behavior
 */
export const queryConfig = {
  /** Data considered fresh for 5 minutes */
  staleTime: 5 * 60 * 1000,
  /** Cache kept for 30 minutes */
  gcTime: 30 * 60 * 1000,
  /** Max retry attempts for failed queries */
  retryCount: 3,
  /** Exponential backoff delay: 1s, 2s, 4s... capped at 30s */
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
} as const;

/**
 * Determines if a query should be retried based on error type
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  // Don't retry on 4xx client errors (except 408 Request Timeout, 429 Too Many Requests)
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
      return false;
    }
  }
  return failureCount < queryConfig.retryCount;
}

/**
 * Create Query Client with sensible defaults
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: queryConfig.staleTime,
      gcTime: queryConfig.gcTime,
      retry: shouldRetry,
      retryDelay: queryConfig.retryDelay,
      // Only refetch on window focus in production
      refetchOnWindowFocus: env.IS_PROD,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
      retryDelay: queryConfig.retryDelay,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Query Provider wrapper
 */
export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {env.IS_DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
    </QueryClientProvider>
  );
}

// Export queryClient for direct access if needed
export { queryClient };
